import React from 'react';
import { Link } from 'react-router-dom';
import MetarWidget from './MetarWidget';
import MeteoblueWidgets from './MeteoblueWidgets';
import { containerClass, Eyebrow, sectionTitleClass } from './ui';
import Picture from './Picture';

type Row = [label: string, value: React.ReactNode];

const linkClass = 'font-semibold text-blue-700 hover:text-blue-600';

const groups: { title: string; rows: Row[] }[] = [
  {
    title: 'Platzdaten',
    rows: [
      ['Ortskennung', 'Bad Langensalza'],
      ['ICAO', 'EDEB'],
      ['Kurzname', 'B-Lang'],
      ['Nord', '51.07.46 N'],
      ['Ost', '10.37.16 E'],
      ['Lage', '1 NM NW Bad Langensalza'],
      ['Ortungshilfen', '251°/34 NM GOT VOR 115.250'],
      ['Höhe', '649 ft / 198 m'],
      ['Funk', '125.365 MHz'],
    ],
  },
  {
    title: 'Start- und Landebahn',
    rows: [
      ['Länge', '700 x 30 m'],
      ['Belag', 'Gras'],
      ['Richtung', '09 / 27'],
      ['Platzrunde', 'UL N 1200 ft MSL'],
      ['Besonderheiten Anflug', 'Überflug Orte vermeiden'],
      ['Danger', <span className="font-semibold text-red-700">Segelflug (Start-/Landeverbot bei gelber Warnblinkleuchte)</span>],
    ],
  },
  {
    title: 'Betrieb & Service',
    rows: [
      ['Betriebszeiten', 'PPR'],
      ['Betriebsarten', 'SLP; GLD; UL; PPG; Ballon; Modellflug'],
      ['UL-Zulassung', '6'],
      ['Tanken', '-'],
      ['Zoll', '-'],
      ['Telefon', <a href="tel:+4936038958503" className={linkClass}>0049 3603 895 8503</a>],
      ['Unterstellmöglichkeit', '-'],
      ['Übernachtung', 'im Ort'],
      ['Camping', 'ja'],
      ['Restauration', 'im Ort'],
      ['Landegebühr', '3,00 €'],
      ['Webseite', <a href="https://flugsportverein.eu" className={linkClass}>flugsportverein.eu</a>],
      ['E-Mail', <a href="mailto:info@flugsportverein.eu" className={linkClass}>info@flugsportverein.eu</a>],
      ['Ausbildung', 'FSV Otto Lilienthal'],
    ],
  },
];

const AirfieldInfo: React.FC = () => {
  return (
    <>
      <div className="h-[220px] overflow-hidden bg-gray-900 lg:h-[360px]">
        <Picture
          src="/images/Flugsportverein_Bilder_11.jpg"
          alt="Luftaufnahme des Flugplatzes Bad Langensalza mit Hangar, Segel- und Ultraleichtflugzeugen"
          sizes="100vw"
          priority
          className="h-full w-full object-cover"
        />
      </div>

      <section id="flugplatzinfo" className={`${containerClass} pb-12 pt-10 lg:pb-[72px] lg:pt-16`}>
        <div className="lg:grid lg:grid-cols-[5fr_7fr] lg:gap-16">
          <div>
            <Eyebrow>Flugplatz EDEB</Eyebrow>
            <h1 className={`${sectionTitleClass} mb-5`}>Flugplatzinformationen für UL Piloten</h1>
            <div role="note" className="border-l-2 border-red-600 bg-red-50 px-4 py-3 text-sm leading-normal text-red-800">
              <p className="font-semibold">Achtung:</p>
              <p>
                Die enthaltenen Daten sind ohne Gewähr und nur als erster Überblick gedacht! Sie dienen nicht für Navigationszwecke! AIP beachten oder Platzhalterauskünfte einholen!
              </p>
            </div>
            <MetarWidget />
          </div>

          <div className="mt-8 flex flex-col gap-8 lg:mt-0">
            {groups.map((group) => (
              <div key={group.title}>
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.1em] text-blue-600 lg:text-[13px]">{group.title}</h2>
                <dl className="border-t-2 border-gray-900 text-[15px] leading-normal">
                  {group.rows.map(([label, value]) => (
                    <div key={label} className="grid grid-cols-[140px_1fr] gap-3 border-b border-gray-200 py-3 lg:grid-cols-[200px_1fr]">
                      <dt className="pt-0.5 text-[13px] text-gray-500">{label}</dt>
                      <dd className="text-gray-900">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>

        <MeteoblueWidgets />

        <Link
          to="/#flugplatz"
          className="mt-10 inline-flex min-h-[48px] items-center gap-3 border border-gray-300 bg-white px-5 text-[15px] font-semibold text-gray-900 transition-colors hover:bg-gray-50"
        >
          <span aria-hidden="true">←</span> zurück
        </Link>
      </section>
    </>
  );
};

export default AirfieldInfo;
